import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/models';
import {
  ProcessedCommissionTransactionResponsePagedResults,
  CreateCommissionProcessedRequest,
  CommissionProcessedResponse,
  GetProcessedCommissionTransactionsParams,
  CreateStaffAdjustmentsRequest,
  MarkCommissionsAsProcessedRequest
} from '../models';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root'
})
export class CommissionProcessedService {
  private http = inject(HttpClient);

  private readonly API_BASE_URL = environment.APP_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  private buildParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          // Handle array parameters
          value.forEach(item => {
            httpParams = httpParams.append(key, item.toString());
          });
        } else {
          httpParams = httpParams.set(key, value.toString());
        }
      }
    });
    
    return httpParams;
  }

  /**
   * GET /api/CommissionProcessed/tenants/{tenantId}/processed
   * Fetch commission transactions for a tenant's staff
   */
  getTenantProcessedTransactions(
    tenantId: number,
    queryParams?: GetProcessedCommissionTransactionsParams
  ): Observable<ProcessedCommissionTransactionResponsePagedResults> {
    const params: Record<string, unknown> = {
      pageNumber: queryParams?.pageNumber || 1,
      pageSize: queryParams?.pageSize || 10
    };

    if (queryParams?.companyId) {
      params['companyId'] = queryParams.companyId;
    }
    if (queryParams?.tenantIds && queryParams.tenantIds.length > 0) {
      params['tenantIds'] = queryParams.tenantIds;
    }
    if (queryParams?.dateFrom) {
      params['dateFrom'] = queryParams.dateFrom;
    }
    if (queryParams?.dateTo) {
      params['dateTo'] = queryParams.dateTo;
    }
    if (queryParams?.staffIds && queryParams.staffIds.length > 0) {
      params['staffIds'] = queryParams.staffIds;
    }
    if (queryParams?.planIds && queryParams.planIds.length > 0) {
      params['planIds'] = queryParams.planIds;
    }
    if (queryParams?.tenantIds && queryParams.tenantIds.length > 0) {
      params['tenantIds'] = queryParams.tenantIds;
    }
    if (queryParams?.transactionStatus) {
      params['transactionStatus'] = queryParams.transactionStatus;
    }

    const url = `${this.API_BASE_URL}${CONST_URL_SEGEMENTS.COMMISSION.GET_COMMISSION_TRANSACTIONS.replace('{tenant_id}', tenantId.toString())}`;

    return this.http.get<ProcessedCommissionTransactionResponsePagedResults>(
      url,
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * POST /api/CommissionProcessed
   * Process a single commission transaction
   */
  createCommissionProcessed(
    request: CreateCommissionProcessedRequest
  ): Observable<CommissionProcessedResponse> {
    return this.http.post<CommissionProcessedResponse>(
      `${this.API_BASE_URL}/CommissionProcessed`,
      request,
      this.httpOptions
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * POST tenants/{tenant_id}/commissions/adjustments
   * Create staff commission adjustments
   */
  createStaffAdjustments(
    tenantId: number,
    request: CreateStaffAdjustmentsRequest
  ): Observable<any> {
    const url = `${this.API_BASE_URL}${CONST_URL_SEGEMENTS.COMMISSION.CREATE_STAFF_COMMISSION_ADJUSTMENTS.replace('{tenant_id}', tenantId.toString())}`;
    return this.http.post<any>(
      url,
      request,
      this.httpOptions
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * POST tenants/{tenant_id}/commissions/mark_as_processed
   * Mark commission transactions as processed
   */
  markCommissionsAsProcessed(
    tenantId: number,
    request: MarkCommissionsAsProcessedRequest
  ): Observable<any> {
    const url = `${this.API_BASE_URL}${CONST_URL_SEGEMENTS.COMMISSION.MARK_COMMISSIONS_AS_PROCESSED.replace('{tenant_id}', tenantId.toString())}`;
    return this.http.post<any>(
      url,
      request,
      this.httpOptions
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * GET /api/CommissionProcessed/{processedId}
   * Get a single processed commission by ID
   */
  getCommissionProcessedById(
    processedId: number,
    companyId?: number
  ): Observable<CommissionProcessedResponse> {
    const params: Record<string, unknown> = {};
    if (companyId) {
      params['companyId'] = companyId;
    }

    return this.http.get<CommissionProcessedResponse>(
      `${this.API_BASE_URL}${CONST_URL_SEGEMENTS.COMMISSION.GET_COMMISSION_PROCESSED_BY_ID.replace('{processed_id}', processedId.toString())}`,
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }
}

